import React, {MutableRefObject, useCallback, useEffect, useReducer, useRef} from 'react';
import {Logger} from '../../utils/managers/logger';
import {AuthContext} from './context';
import {
  AuthContextActionType,
  AuthContextState,
  SignInCredentials,
  VoximplantIncomingPushDisposition,
} from './types';
import {
  getAuth,
  signInWithEmailAndPassword,
  User as FirebaseUser,
  UserCredential,
  OAuthProvider,
  signInWithPopup,
  GoogleAuthProvider,
} from 'firebase/auth';
import {SignInError} from '../../enums/sign-in-error';
import {
  BackendApiManager,
  BackendApiManagerEventEmitter,
  BackendApiManagerEvents,
} from '../../managers/backend-api';
import {MeRepository} from '../../repositories/me';
import {Me} from '../../backend/interfaces/me';
import {useAppContext} from '../app/context';
import {updateAppLanguage} from '../../i18n/functions';
import {InviteIdentification} from '../../interfaces/invite-identification';
import {Session} from '../../interfaces/session';
import {BackendLanguageTag} from '../../backend/enums/backend-language-tag';
import {RelationshipInfo} from '../../backend/interfaces/relationship-info';
import {AffiliationInfo} from '../../backend/interfaces/affiliation-info';
import {User} from '../../backend/interfaces/user';
import {VideocallStateParticipantInfo, VoximplantProcessState} from '../office/types';
import {useOffice} from '../office/context';
import {PushNotificationsState} from '../../enums/push-notifications-state';
import {
  deleteToken,
  getMessaging,
  getToken,
  isSupported,
  Messaging,
  onMessage,
} from 'firebase/messaging';
import {FirebaseApp, getApp} from 'firebase/app';
import {getInstance, ConnectionNode, Events, ClientState} from 'voximplant-websdk';
import {AppConfig} from '../../interfaces/app-config';
import {OperativeSystemName} from '../../backend/enums/operative-system-name';
import {v4 as uuidv4} from 'uuid';
import {AppLanguageTag} from '../../i18n/types';
import {NotificationsRepository} from '../../repositories/notifications';
import {PutNotificationsSubscription} from '../../backend/requests/put-notification-subscription';
import {LocalStorageKey} from '../../enums/local-storage-key';
import i18n from 'i18next';
import reducer from './reducer';
import {ExternalAccount} from '../../backend/interfaces/external-account';
import {CallExternalAccountData} from '../../backend/interfaces/call-external-account-data';
import {ExternalAccountType} from '../../backend/enums/external-account-type';
import {RolePermissionType} from '../../backend/enums/role-permission-type';
import {createSessionData} from '../../utils/functions/create-session-data';
import {SessionAffiliationInfo} from '../../interfaces/session-affiliation-info';
import {QuickAccessManager} from '../../managers/quick-access-manager';
import {NotificationType} from '../../backend/enums/notification-type';
import {wait} from '../../utils/functions/wait';
import eventEmitter from '../../events/event-emitter';
import {PUSH_NOTIFICATION_RECEIVED} from '../../events/event-types';

const QuickAccessSessionDuration = 600000; // 10 minutes

// Notes
// - If you go to Chrome settings and change the notifications permissions to "ask" again,
// after reloading the app and the user gives permissions again, a warning appears saying
// "A problem occurred while unsubscribing the user from FCM: Requested entity was not found. (messaging/token-unsubscribe-failed).".
// We ignore this for now because it's a problem on Firebase. Event if we receive this warning
// we are able to subscribe to push notifications successfully.
// https://github.com/firebase/firebase-js-sdk/issues/2364#issuecomment-570820017,
// https://stackoverflow.com/questions/58325574/firebase-fcm-javascript-error-when-request-permission-error-status-500

// TODO - Update notifications in real-time?
//      - When a push is received
//      - On Safari, check every X minutes to see if new notifications were created

// Loggers
const logger: Logger = new Logger('AuthContext');
const voxiLogger: Logger = new Logger('Voximplant');

// Set initial state
const initialState: AuthContextState = {
  isRestoringSession: true,
  pushNotificationsState: PushNotificationsState.Unknown,
  firebaseUser: undefined,
  session: undefined,
};

// onAuthStateChanged Subscription
let onAuthStateChangedSubscription: any;

export const AuthContextProvider = (props: {children: React.ReactNode}) => {
  // Get context
  // Note: Since we are using, other contexts here we need to make sure that they are always parent
  // components of out auth context provider. Otherwise, we won't be able to access to in here.
  const appContext = useAppContext();
  const officeContext = useOffice();
  const [state, dispatch] = useReducer(reducer, initialState);

  // We keep refs for state used inside listeners/callbacks to avoid stale closures.
  const upToDateSessionRef: MutableRefObject<Session | undefined> = useRef(state.session);
  const upToDateVoximplantStateRef = useRef(officeContext.state.voximplant);
  const upToDateVideocallStateRef = useRef(officeContext.state.videocall);
  const upToDateIncomingCallRef = useRef(officeContext.state.incomingCall);
  const pendingForegroundVoximplantPushRef = useRef<any>();
  const lastTransportWarningAtRef = useRef<number>(0);
  const reconnectVoximplantTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const noConnectionAtAllTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const healthyIncomingPushRetryTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const lastHealthyIncomingPushRetryKeyRef = useRef<string | undefined>();
  const reconnectVoximplantWhenOnlineRef = useRef<boolean>(false);
  const connectToVoximplantInProgressRef = useRef<boolean>(false);
  const signInVoximplantInProgressRef = useRef<boolean>(false);
  const connectionClosedHandlerRef = useRef<((event: any) => void) | undefined>();
  const connectionEstablishedHandlerRef = useRef<((event: any) => void) | undefined>();
  const quickAccessSessionTimeout = useRef<ReturnType<typeof setTimeout>>();

  // Update refs when state changes
  useEffect(() => {
    upToDateSessionRef.current = state.session;
  }, [state.session]);

  useEffect(() => {
    upToDateVoximplantStateRef.current = officeContext.state.voximplant;
  }, [officeContext.state.voximplant]);

  useEffect(() => {
    upToDateVideocallStateRef.current = officeContext.state.videocall;
  }, [officeContext.state.videocall]);

  useEffect(() => {
    upToDateIncomingCallRef.current = officeContext.state.incomingCall;
  }, [officeContext.state.incomingCall]);

  // #region Voximplant

  const patchVoximplantState = useCallback(
    (patch: Partial<(typeof officeContext.state)['voximplant']>) => {
      const nextVoximplantState = {...upToDateVoximplantStateRef.current, ...patch};
      upToDateVoximplantStateRef.current = nextVoximplantState;
      officeContext.updateVoximplantState(nextVoximplantState);
    },
    [officeContext]
  );

  const getVoximplantCredentials = useCallback((session?: Session) => {
    if (!session?.externalAccounts || !Array.isArray(session.externalAccounts)) {
      return undefined;
    }

    const callExternalAccount: ExternalAccount<CallExternalAccountData> | undefined =
      session.externalAccounts.find((obj) => obj.type === ExternalAccountType.Call);

    if (
      !callExternalAccount?.data?.applicationName ||
      !callExternalAccount.data.password ||
      !session.id
    ) {
      return undefined;
    }

    return {
      username: `${session.id}@${callExternalAccount.data.applicationName}`,
      password: callExternalAccount.data.password,
      callExternalAccount,
    };
  }, []);

  const getCallerInfoFromVoximplantNotification = useCallback((notification: any) => {
    if (!notification?.voximplant) {
      return undefined;
    }

    try {
      const payload = JSON.parse(notification.voximplant);
      const displayName = payload.display_name || payload.displayName;

      if (!displayName) {
        return undefined;
      }

      return {
        id: payload.callid || displayName,
        name: displayName,
        profilePicture: payload.profile_picture || payload.profilePicture || '',
      } as VideocallStateParticipantInfo;
    } catch (error) {
      voxiLogger.error('Could not parse caller info from queued Voximplant notification.', error);
      return undefined;
    }
  }, []);

  const getCallIdFromVoximplantNotification = useCallback((notification: any) => {
    if (!notification?.voximplant) {
      return undefined;
    }

    try {
      return JSON.parse(notification.voximplant).callid as string | undefined;
    } catch (error) {
      voxiLogger.error('Could not parse call id from queued Voximplant notification.', error);
      return undefined;
    }
  }, []);

  const showPendingIncomingVoximplantCallPlaceholder = useCallback(
    (notification: any, reason: string) => {
      const videocallState = upToDateVideocallStateRef.current;

      if (
        videocallState.currentCall ||
        (videocallState.callIncoming && !videocallState.callIncomingPendingRecovery)
      ) {
        return;
      }

      officeContext.updateVideocallState({
        ...videocallState,
        callIncoming: true,
        callIncomingAction: undefined,
        callIncomingPendingRecovery: true,
        callerInfo:
          getCallerInfoFromVoximplantNotification(notification) ?? videocallState.callerInfo,
      });

      voxiLogger.debug('Showing incoming-call placeholder while Voximplant recovers.', {
        reason,
      });
    },
    [getCallerInfoFromVoximplantNotification, officeContext]
  );

  const clearPendingIncomingVoximplantCallPlaceholder = useCallback(
    (reason: string) => {
      const videocallState = upToDateVideocallStateRef.current;

      if (!videocallState.callIncomingPendingRecovery) {
        return;
      }

      officeContext.updateVideocallState({
        ...videocallState,
        callIncoming: false,
        callIncomingAction: undefined,
        callIncomingPendingRecovery: false,
        callerInfo: videocallState.currentCall ? videocallState.callerInfo : undefined,
      });

      voxiLogger.debug(
        'Hiding incoming-call placeholder after Voximplant recovery state changed.',
        {
          reason,
        }
      );
    },
    [officeContext]
  );

  const clearPendingForegroundVoximplantPushRecovery = useCallback(
    (reason: string, options?: {clearPlaceholder?: boolean}) => {
      const hadPendingPush = !!pendingForegroundVoximplantPushRef.current;
      const hadHealthyRetryTimeout = !!healthyIncomingPushRetryTimeoutRef.current;
      const hadHealthyRetryKey = !!lastHealthyIncomingPushRetryKeyRef.current;

      if (healthyIncomingPushRetryTimeoutRef.current) {
        clearTimeout(healthyIncomingPushRetryTimeoutRef.current);
        healthyIncomingPushRetryTimeoutRef.current = undefined;
      }

      lastHealthyIncomingPushRetryKeyRef.current = undefined;
      pendingForegroundVoximplantPushRef.current = undefined;

      if (options?.clearPlaceholder) {
        clearPendingIncomingVoximplantCallPlaceholder(reason);
      }

      if (hadPendingPush || hadHealthyRetryTimeout || hadHealthyRetryKey) {
        voxiLogger.debug('Cleared pending incoming Voximplant push recovery state.', {
          reason,
          clearPlaceholder: !!options?.clearPlaceholder,
        });
      }
    },
    [clearPendingIncomingVoximplantCallPlaceholder]
  );

  const clearPendingIncomingVoximplantCallPlaceholderFromMissedCall = useCallback(
    (notification: any, reason: string) => {
      if (notification?.notificationType !== NotificationType.MissedCall) {
        return false;
      }

      const videocallState = upToDateVideocallStateRef.current;

      if (!videocallState.callIncomingPendingRecovery) {
        return false;
      }

      const missedCallId = notification.callId;
      const missedCallerName = notification.callerName;
      const pendingPush = pendingForegroundVoximplantPushRef.current;
      const pendingNotification = pendingPush?.notification ?? pendingPush;
      const pendingCallId = getCallIdFromVoximplantNotification(pendingNotification);
      const pendingCallerName = getCallerInfoFromVoximplantNotification(pendingNotification)?.name;
      const currentIncomingCall = upToDateIncomingCallRef.current;
      const currentIncomingCallerName = getCallerInfoFromVoximplantNotification(
        currentIncomingCall?.notification
      )?.name;

      const matchesPendingPush =
        (!!missedCallId && missedCallId === pendingCallId) ||
        (!!missedCallerName && missedCallerName === pendingCallerName);
      const matchesCurrentIncomingCall =
        (!!missedCallId && missedCallId === currentIncomingCall?.callId) ||
        (!!missedCallerName && missedCallerName === currentIncomingCallerName);
      const matchesVisiblePlaceholderCaller =
        !!missedCallerName && missedCallerName === videocallState.callerInfo?.name;

      if (!matchesPendingPush && !matchesCurrentIncomingCall && !matchesVisiblePlaceholderCaller) {
        return false;
      }

      if (matchesCurrentIncomingCall) {
        officeContext.setIncomingCall(undefined);
      }

      clearPendingForegroundVoximplantPushRecovery(reason, {
        clearPlaceholder: true,
      });
      voxiLogger.debug(
        'Clearing incoming-call recovery placeholder because a matching missed-call notification was received.',
        {
          reason,
          missedCallId,
          pendingCallId,
        }
      );

      return true;
    },
    [
      clearPendingForegroundVoximplantPushRecovery,
      getCallIdFromVoximplantNotification,
      getCallerInfoFromVoximplantNotification,
      officeContext,
    ]
  );

  const flushPendingForegroundVoximplantPush = useCallback(
    (reason: string) => {
      const pendingPush = pendingForegroundVoximplantPushRef.current;
      const pendingNotification = pendingPush?.notification ?? pendingPush;
      const pendingAction = pendingPush?.action ?? '';

      if (!pendingNotification?.voximplant) {
        clearPendingForegroundVoximplantPushRecovery(reason, {
          clearPlaceholder: true,
        });
        return;
      }

      if (
        upToDateVideocallStateRef.current.callIncoming &&
        !upToDateVideocallStateRef.current.callIncomingPendingRecovery
      ) {
        clearPendingForegroundVoximplantPushRecovery(
          'skipping queued incoming Voximplant push because an incoming call is already shown',
          {
            clearPlaceholder: true,
          }
        );
        return;
      }

      try {
        const parsedPayload = JSON.parse(pendingNotification.voximplant);

        voxiLogger.debug('Flushing queued foreground Voximplant push.', {
          reason,
          callId: parsedPayload.callid,
        });

        clearPendingIncomingVoximplantCallPlaceholder(reason);

        officeContext.setIncomingCall({
          callId: parsedPayload.callid,
          action: pendingAction,
          appAlreadyOpened: false,
          notification: pendingNotification,
        });
        clearPendingForegroundVoximplantPushRecovery(reason);
      } catch (error) {
        voxiLogger.error('Could not flush queued foreground Voximplant push.', error);
        clearPendingForegroundVoximplantPushRecovery(
          'queued incoming Voximplant push could not be flushed',
          {
            clearPlaceholder: true,
          }
        );
      }
    },
    [
      clearPendingForegroundVoximplantPushRecovery,
      clearPendingIncomingVoximplantCallPlaceholder,
      officeContext,
    ]
  );

  // Voximplant SignOut
  const signOutVoximplant = async () => {
    // Update UI
    patchVoximplantState({
      signedOutState: VoximplantProcessState.InProgress,
      degraded: true,
    });

    try {
      // Get SDK
      const sdk = getInstance();

      if (reconnectVoximplantTimeoutRef.current) {
        clearTimeout(reconnectVoximplantTimeoutRef.current);
        reconnectVoximplantTimeoutRef.current = undefined;
      }

      if (healthyIncomingPushRetryTimeoutRef.current) {
        clearTimeout(healthyIncomingPushRetryTimeoutRef.current);
        healthyIncomingPushRetryTimeoutRef.current = undefined;
      }

      clearTimeout(noConnectionAtAllTimeoutRef.current);
      noConnectionAtAllTimeoutRef.current = undefined;

      connectToVoximplantInProgressRef.current = false;
      signInVoximplantInProgressRef.current = false;
      lastHealthyIncomingPushRetryKeyRef.current = undefined;
      reconnectVoximplantWhenOnlineRef.current = false;
      connectionClosedHandlerRef.current = undefined;
      connectionEstablishedHandlerRef.current = undefined;
      pendingForegroundVoximplantPushRef.current = undefined;
      clearPendingIncomingVoximplantCallPlaceholder('signOutVoximplant');

      // Remove listeners
      sdk.off(Events.ConnectionClosed);
      sdk.off(Events.ConnectionEstablished);

      // Sign in
      await sdk.disconnect();

      // Log
      voxiLogger.debug('Signed out of Voximplant.');

      // Update UI
      patchVoximplantState({
        signedOutState: VoximplantProcessState.Done,
        connectionState: VoximplantProcessState.Undefined,
        signedInState: VoximplantProcessState.Undefined,
        degraded: false,
      });
    } catch (e: any) {
      // Log
      voxiLogger.error('Could not sign out of Voximplant.', e.message);

      // Update UI
      patchVoximplantState({
        signedOutState: VoximplantProcessState.Failed,
        degraded: true,
      });
    }
  };

  // Voximplant SignIn
  const signInVoximplant = async (username: string, password: string) => {
    // Get SDK
    const sdk = getInstance();
    const clientState = sdk.getClientState();

    if (clientState === ClientState.LOGGED_IN) {
      voxiLogger.debug('Skipping Voximplant sign-in because the SDK is already logged in.');
      lastTransportWarningAtRef.current = 0;
      patchVoximplantState({
        connectionState: VoximplantProcessState.Done,
        signedInState: VoximplantProcessState.Done,
        signedOutState: VoximplantProcessState.Undefined,
        degraded: false,
      });
      flushPendingForegroundVoximplantPush('signInVoximplant already logged in');
      return;
    }

    if (clientState === ClientState.LOGGING_IN) {
      voxiLogger.debug('Skipping Voximplant sign-in because the SDK is already logging in.');
      patchVoximplantState({
        connectionState: VoximplantProcessState.Done,
        signedInState: VoximplantProcessState.InProgress,
        signedOutState: VoximplantProcessState.Undefined,
      });
      return;
    }

    if (signInVoximplantInProgressRef.current) {
      voxiLogger.debug('Skipping Voximplant sign-in because a sign-in is already in progress.');
      return;
    }

    signInVoximplantInProgressRef.current = true;

    // Update UI
    patchVoximplantState({
      signedInState: VoximplantProcessState.InProgress,
      signedOutState: VoximplantProcessState.Undefined,
    });

    try {
      // Sign in
      await sdk.login(username, password);

      // Log
      voxiLogger.debug('Signed in Voximplant as ' + username);

      lastTransportWarningAtRef.current = 0;

      patchVoximplantState({
        connectionState: VoximplantProcessState.Done,
        signedInState: VoximplantProcessState.Done,
        degraded: false,
      });

      flushPendingForegroundVoximplantPush('signInVoximplant');
    } catch (e: any) {
      // Log
      voxiLogger.error('Could not sign in Voximplant.', e.message);

      patchVoximplantState({
        signedInState: VoximplantProcessState.Failed,
        degraded: true,
      });
    } finally {
      signInVoximplantInProgressRef.current = false;
    }
  };

  // Voximplant Connect
  const connectToVoximplant = useCallback(
    async (username: string, password: string) => {
      // Get SDK
      const sdk = getInstance();
      const clientState = sdk.getClientState();

      if (clientState === ClientState.LOGGED_IN) {
        voxiLogger.debug('Skipping Voximplant connect because the SDK is already logged in.');
        patchVoximplantState({
          connectionState: VoximplantProcessState.Done,
          signedInState: VoximplantProcessState.Done,
        });
        flushPendingForegroundVoximplantPush('connectToVoximplant already logged in');
        return;
      }

      if (
        clientState === ClientState.CONNECTED ||
        clientState === ClientState.CONNECTING ||
        clientState === ClientState.RECONNECTING ||
        clientState === ClientState.LOGGING_IN
      ) {
        voxiLogger.debug(
          'Skipping Voximplant connect because the SDK is already connecting or connected.',
          {
            clientState,
          }
        );
        patchVoximplantState({
          connectionState:
            clientState === ClientState.CONNECTED || clientState === ClientState.LOGGING_IN
              ? VoximplantProcessState.Done
              : VoximplantProcessState.InProgress,
        });
        return;
      }

      if (connectToVoximplantInProgressRef.current) {
        voxiLogger.debug('Skipping Voximplant connect because a connect is already in progress.');
        return;
      }

      connectToVoximplantInProgressRef.current = true;

      // Update UI
      patchVoximplantState({
        connectionState: VoximplantProcessState.InProgress,
      });

      try {
        // Connect
        await sdk.connect();

        const clientStateAfterConnect = sdk.getClientState();
        const connectionLooksEstablished =
          clientStateAfterConnect === ClientState.CONNECTED ||
          clientStateAfterConnect === ClientState.LOGGING_IN ||
          clientStateAfterConnect === ClientState.LOGGED_IN;

        if (!connectionLooksEstablished) {
          voxiLogger.debug(
            'Voximplant connect resolved but the SDK is not in a connected state yet. Waiting for ConnectionEstablished or ConnectionClosed instead of marking the connection as healthy.',
            {
              clientStateAfterConnect,
            }
          );

          patchVoximplantState({
            connectionState: VoximplantProcessState.InProgress,
            signedInState: VoximplantProcessState.Undefined,
          });

          // Try manually if no connection established.
          clearTimeout(noConnectionAtAllTimeoutRef.current);
          noConnectionAtAllTimeoutRef.current = setTimeout(async () => {
            voxiLogger.debug(
              'No healthy connection established yet after Voximplant connect. Manually re-trying..',
              {
                clientState: sdk.getClientState(),
              }
            );
            await signOutVoximplant();
            await wait(3_000);
            if (upToDateSessionRef.current) await Voximplant(upToDateSessionRef.current);
          }, 20_000);
          return;
        }

        voxiLogger.debug(
          'Voximplant connect resolved. Waiting for ConnectionEstablished/sign-in.',
          {
            clientStateAfterConnect,
          }
        );

        clearTimeout(noConnectionAtAllTimeoutRef.current);
        noConnectionAtAllTimeoutRef.current = undefined;

        patchVoximplantState({
          connectionState: VoximplantProcessState.InProgress,
        });
      } catch (e: any) {
        // Log
        voxiLogger.error('Could not connect to Voximplant.', e.message);

        patchVoximplantState({
          connectionState: VoximplantProcessState.Failed,
          degraded: true,
        });
      } finally {
        connectToVoximplantInProgressRef.current = false;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [flushPendingForegroundVoximplantPush, patchVoximplantState]
  );

  // Voximplant Init
  const initVoximplant = useCallback(
    async (username: string, password: string) => {
      // Get SDK
      const sdk = getInstance();

      // Update UI
      patchVoximplantState({
        sdkVersion: sdk.version,
        initState: VoximplantProcessState.InProgress,
      });

      try {
        // Init
        await sdk.init({
          localVideoContainerId: 'local_video_holder', // Id of HTMLElement that is used as a default container for local video elements
          // serverIp: serverIpInput.value, // IP address of a particular media gateway for connection. If it's not specified, IP address will be chosen automatically
          showDebugInfo: false, // window.location.hostname === 'localhost', // Show debug info in the console
          node: ConnectionNode.NODE_8, // https://manage.voximplant.com/ -> Credentials for working with API, SDK, SIP
        });

        // Update UI
        patchVoximplantState({
          initState: VoximplantProcessState.Done,
        });

        // Connect
        await connectToVoximplant(username, password);
      } catch (e: any) {
        // Log
        voxiLogger.error('Could not initialize Voximplant.', e.message);

        // Update UI
        patchVoximplantState({
          initState: VoximplantProcessState.Failed,
          degraded: true,
        });
      }
    },
    [connectToVoximplant, patchVoximplantState]
  );

  const handleVoximplantLoggerRecord = useCallback(
    (record: any) => {
      const formattedText = typeof record?.formattedText === 'string' ? record.formattedText : '';
      const transportWarningDetected =
        formattedText.includes('WARNING WSTransport') &&
        formattedText.includes('has no close handler');
      // const sdkErrorDetected = formattedText.includes('ERROR');

      if (transportWarningDetected) {
        lastTransportWarningAtRef.current = Date.now();
      }

      if (transportWarningDetected) {
        patchVoximplantState({
          degraded: true,
        });
      }
    },
    [patchVoximplantState]
  );

  const deferVoximplantIncomingPushUntilHealthy = useCallback(
    (notification: any, action: string, reason: string): VoximplantIncomingPushDisposition => {
      let callId: string | undefined;
      try {
        callId = JSON.parse(notification?.voximplant ?? '{}').callid;
      } catch (error) {
        voxiLogger.error(
          'Could not parse Voximplant notification while evaluating whether it should be queued.',
          {
            reason,
            error,
          }
        );
      }

      const activeVideocallState = upToDateVideocallStateRef.current;
      const duplicateIncomingAlreadyVisible =
        activeVideocallState.callIncoming && !activeVideocallState.callIncomingPendingRecovery;

      if (duplicateIncomingAlreadyVisible) {
        voxiLogger.debug(
          'Ignoring incoming Voximplant push because the incoming call UI is already visible.',
          {
            reason,
            callId,
          }
        );
        return 'duplicate-visible-ui';
      }

      pendingForegroundVoximplantPushRef.current = {
        notification,
        action,
      };

      const voximplantState = upToDateVoximplantStateRef.current;
      const transportWarningRecentlySeen = Date.now() - lastTransportWarningAtRef.current < 10000;
      const voximplantReady =
        voximplantState.connectionState === VoximplantProcessState.Done &&
        voximplantState.signedInState === VoximplantProcessState.Done;

      if (!voximplantReady || transportWarningRecentlySeen) {
        voxiLogger.debug(
          'Queueing incoming Voximplant push until Voximplant connection is healthy again.',
          {
            reason,
            callId,
          }
        );
        showPendingIncomingVoximplantCallPlaceholder(notification, reason);
        return 'queued-until-healthy';
      }

      return 'ready';
    },
    [showPendingIncomingVoximplantCallPlaceholder]
  );

  const queueForegroundVoximplantPush = useCallback(
    (notification: any) => {
      const disposition = deferVoximplantIncomingPushUntilHealthy(
        notification,
        '',
        'foreground Voximplant push'
      );

      if (disposition !== 'ready') {
        return;
      }

      flushPendingForegroundVoximplantPush('foreground Voximplant push');
    },
    [deferVoximplantIncomingPushUntilHealthy, flushPendingForegroundVoximplantPush]
  );

  useEffect(() => {
    const handleOnline = async () => {
      if (!reconnectVoximplantWhenOnlineRef.current) {
        return;
      }

      reconnectVoximplantWhenOnlineRef.current = false;

      const credentials = getVoximplantCredentials(upToDateSessionRef.current);
      if (!credentials) {
        voxiLogger.debug(
          'Skipping delayed Voximplant reconnect after the browser came back online because no call account is available.'
        );
        return;
      }

      const sdk = getInstance();
      const clientState = sdk.getClientState();
      const selfRecoveryStarted =
        clientState === ClientState.CONNECTING ||
        clientState === ClientState.RECONNECTING ||
        clientState === ClientState.LOGGING_IN ||
        clientState === ClientState.CONNECTED ||
        clientState === ClientState.LOGGED_IN;

      if (selfRecoveryStarted) {
        voxiLogger.debug(
          'Skipping delayed Voximplant reconnect because the SDK already started recovering after the browser came back online.',
          {
            clientState,
          }
        );
        return;
      }

      voxiLogger.debug(
        'Browser came back online after ConnectionClosed. Reconnecting Voximplant manually.',
        {
          clientState,
        }
      );

      await wait(1500);
      void connectToVoximplant(credentials.username, credentials.password);
    };

    window.addEventListener('online', handleOnline);
    return () => {
      window.removeEventListener('online', handleOnline);
    };
  }, [connectToVoximplant, getVoximplantCredentials]);

  const recoverVoximplantIncomingPush = useCallback(
    async (notification: any, reason: string, action: string = '') => {
      if (!notification?.voximplant) {
        return;
      }

      pendingForegroundVoximplantPushRef.current = {
        notification,
        action,
      };

      let callId: string | undefined;
      try {
        callId = JSON.parse(notification.voximplant).callid;
      } catch (error) {
        voxiLogger.error('Could not parse Voximplant notification queued for recovery.', {
          reason,
          error,
        });
      }

      const credentials = getVoximplantCredentials(upToDateSessionRef.current);

      if (!credentials) {
        voxiLogger.debug(
          'Skipping Voximplant recovery for pending incoming push because no call account is available.',
          {
            reason,
            callId,
          }
        );
        clearPendingForegroundVoximplantPushRecovery(reason, {
          clearPlaceholder: true,
        });
        return;
      }

      const sdk = getInstance();
      const clientState = sdk.getClientState();
      const transportWarningRecentlySeen = Date.now() - lastTransportWarningAtRef.current < 10000;

      voxiLogger.debug('Evaluating Voximplant recovery for pending incoming push.', {
        reason,
        callId,
      });

      if (!sdk.alreadyInitialized) {
        showPendingIncomingVoximplantCallPlaceholder(notification, reason);
        await initVoximplant(credentials.username, credentials.password);
        return;
      }

      if (transportWarningRecentlySeen) {
        voxiLogger.debug(
          'Keeping the pending incoming push queued because recent Voximplant transport warnings were seen. Waiting for Voximplant to recover on its own or emit ConnectionClosed.',
          {
            reason,
            callId,
          }
        );
        showPendingIncomingVoximplantCallPlaceholder(notification, reason);
        return;
      }

      if (
        clientState === ClientState.CONNECTING ||
        clientState === ClientState.RECONNECTING ||
        clientState === ClientState.LOGGING_IN
      ) {
        voxiLogger.debug(
          'Keeping the pending incoming push queued while Voximplant finishes its own recovery.',
          {
            reason,
            callId,
          }
        );
        showPendingIncomingVoximplantCallPlaceholder(notification, reason);
        return;
      }

      if (clientState === ClientState.CONNECTED || clientState === ClientState.LOGGED_IN) {
        const notificationKey = notification.voximplant;

        if (lastHealthyIncomingPushRetryKeyRef.current === notificationKey) {
          voxiLogger.debug(
            'Keeping the pending incoming push queued after the healthy-session retry failed. Waiting for an actual ConnectionClosed before attempting any reconnect.',
            {
              reason,
              callId,
            }
          );
          return;
        }

        lastHealthyIncomingPushRetryKeyRef.current = notificationKey;

        if (healthyIncomingPushRetryTimeoutRef.current) {
          clearTimeout(healthyIncomingPushRetryTimeoutRef.current);
          healthyIncomingPushRetryTimeoutRef.current = undefined;
        }

        voxiLogger.debug(
          'Retrying the pending incoming push once more without reconnect because Voximplant is still connected.',
          {
            reason,
            callId,
          }
        );

        showPendingIncomingVoximplantCallPlaceholder(notification, reason);

        healthyIncomingPushRetryTimeoutRef.current = setTimeout(() => {
          healthyIncomingPushRetryTimeoutRef.current = undefined;
          flushPendingForegroundVoximplantPush('healthy connected incoming push retry');
        }, 1500);
        return;
      }

      voxiLogger.debug(
        'Keeping the pending incoming push queued because Voximplant is not currently connected. Waiting for the ConnectionClosed recovery flow instead of reconnecting manually from push recovery.',
        {
          reason,
          callId,
        }
      );
      showPendingIncomingVoximplantCallPlaceholder(notification, reason);
    },
    [
      clearPendingForegroundVoximplantPushRecovery,
      flushPendingForegroundVoximplantPush,
      getVoximplantCredentials,
      initVoximplant,
      showPendingIncomingVoximplantCallPlaceholder,
    ]
  );

  // Voximplant Resume
  const Voximplant = async (session: Session): Promise<boolean> => {
    // Flag
    let accountAvailable: boolean = false;

    // Validate if we have all the data that we need
    if (session.externalAccounts && Array.isArray(session.externalAccounts)) {
      // Get Vox Implant external account
      const callExternalAccount: ExternalAccount<CallExternalAccountData> | undefined =
        session.externalAccounts.find((obj) => obj.type === ExternalAccountType.Call);

      // Validate
      if (
        callExternalAccount &&
        callExternalAccount.data &&
        callExternalAccount.data.applicationName &&
        callExternalAccount.data.password
      ) {
        // We have all that we need to Connect to Voximplant
        voxiLogger.debug('Caregiver Call External Account found. Connecting...', {
          ownerId: callExternalAccount.ownerId,
          ownerType: callExternalAccount.ownerType,
          type: callExternalAccount.type,
          applicationName: callExternalAccount.data.applicationName,
        });

        // Update flag
        accountAvailable = true;

        // Get SDK
        const sdk = getInstance();

        // Build username
        const username: string = `${session.id}@${callExternalAccount.data.applicationName}`;
        const password: string = callExternalAccount.data.password;

        sdk.setLoggerCallback(handleVoximplantLoggerRecord);
        sdk.enableSilentLogging(true);

        if (reconnectVoximplantTimeoutRef.current) {
          clearTimeout(reconnectVoximplantTimeoutRef.current);
          reconnectVoximplantTimeoutRef.current = undefined;
        }

        if (connectionClosedHandlerRef.current) {
          sdk.off(Events.ConnectionClosed, connectionClosedHandlerRef.current);
        }

        if (connectionEstablishedHandlerRef.current) {
          sdk.off(Events.ConnectionEstablished, connectionEstablishedHandlerRef.current);
        }

        connectionClosedHandlerRef.current = (event) => {
          voxiLogger.debug('Voximplant SDK connection closed.', event);

          connectToVoximplantInProgressRef.current = false;
          signInVoximplantInProgressRef.current = false;
          reconnectVoximplantWhenOnlineRef.current = false;

          if (healthyIncomingPushRetryTimeoutRef.current) {
            clearTimeout(healthyIncomingPushRetryTimeoutRef.current);
            healthyIncomingPushRetryTimeoutRef.current = undefined;
          }

          lastHealthyIncomingPushRetryKeyRef.current = undefined;

          patchVoximplantState({
            connectionState: VoximplantProcessState.Failed,
            signedInState: VoximplantProcessState.Undefined,
            signedOutState: VoximplantProcessState.Undefined,
            degraded: true,
          });

          if (!sdk.isRTCsupported()) {
            return;
          }

          if (reconnectVoximplantTimeoutRef.current) {
            clearTimeout(reconnectVoximplantTimeoutRef.current);
          }

          reconnectVoximplantTimeoutRef.current = setTimeout(() => {
            reconnectVoximplantTimeoutRef.current = undefined;

            const clientState = sdk.getClientState();
            const selfRecoveryStarted =
              clientState === ClientState.CONNECTING ||
              clientState === ClientState.RECONNECTING ||
              clientState === ClientState.LOGGING_IN ||
              clientState === ClientState.CONNECTED ||
              clientState === ClientState.LOGGED_IN;

            if (selfRecoveryStarted) {
              voxiLogger.debug(
                'Skipping manual Voximplant reconnect because the SDK already started recovering after ConnectionClosed.',
                {
                  clientState,
                }
              );
              return;
            }

            if (navigator.onLine === false) {
              reconnectVoximplantWhenOnlineRef.current = true;
              voxiLogger.debug(
                'Skipping manual Voximplant reconnect because the browser is offline after ConnectionClosed. Waiting for the browser to come back online.',
                {
                  clientState,
                }
              );
              return;
            }

            voxiLogger.debug(
              'Voximplant did not start recovering after ConnectionClosed. Reconnecting manually.',
              {
                clientState,
              }
            );
            void connectToVoximplant(username, password);
          }, 5_000);
        };

        connectionEstablishedHandlerRef.current = async () => {
          reconnectVoximplantWhenOnlineRef.current = false;

          if (reconnectVoximplantTimeoutRef.current) {
            clearTimeout(reconnectVoximplantTimeoutRef.current);
            reconnectVoximplantTimeoutRef.current = undefined;
          }

          patchVoximplantState({
            connectionState: VoximplantProcessState.Done,
            signedOutState: VoximplantProcessState.Undefined,
          });

          try {
            await signInVoximplant(username, password);
          } catch (e: any) {
            voxiLogger.error('Could not sign in Voximplant.', e.message);
          }
        };

        sdk.on(Events.ConnectionClosed, connectionClosedHandlerRef.current);
        sdk.on(Events.ConnectionEstablished, connectionEstablishedHandlerRef.current);

        if (!sdk.isRTCsupported()) {
          // Log
          voxiLogger.error('WebRTC is not supported in this browser.');
          // Update UI
          patchVoximplantState({
            connectionState: VoximplantProcessState.Failed,
            degraded: true,
          });
          return Promise.resolve(accountAvailable);
        }

        // Check SDK init
        if (sdk.alreadyInitialized) {
          // Connect
          await connectToVoximplant(username, password);
        } else {
          // Init
          await initVoximplant(username, password);
        }
      }
    }

    // Check
    if (!accountAvailable) {
      // Log
      voxiLogger.warn('Current caregiver does not have a Call External Account.', session.id);
    }

    // Return state
    return accountAvailable;
  };

  // Subscribe to Incoming Calls Push Notification
  const registerNotificationSubscriptionOnVoximplant = async (token: string) => {
    // Do it
    try {
      // Get SDK
      const sdk = getInstance();

      // Register
      await sdk.registerForPushNotifications(token);

      // Log
      voxiLogger.debug('Subscribed to Voximplant Incoming Call Push Notifications');
    } catch (e: any) {
      // Log
      voxiLogger.error('Could not subscribe to Incoming Call Push Notifications.', e);
    }
  };

  // Unsubscribe to Incoming Calls Push Notification
  const unregisterNotificationSubscriptionOnVoximplant = async (token: string) => {
    // Do it
    try {
      // Get SDK
      const sdk = getInstance();

      // Register
      await sdk.unregisterForPushNotifications(token);

      // Log
      voxiLogger.debug('Unubscribed from Voximplant Incoming Call Push Notifications');
    } catch (e: any) {
      // Log
      voxiLogger.error('Could not unsubscribe from Incoming Call Push Notifications.', e);
    }
  };

  // #endregion

  const restorePreviousSession_withFirebaseUser = async (firebaseUser: FirebaseUser) => {
    try {
      // Load session
      const me: Me = await MeRepository.instance.get();

      // Update the user language
      await updateAppLanguage(
        me && me.user && me.user.languageCode ? me.user.languageCode : undefined
      );

      // Selected relationship
      const selectedRelationship: RelationshipInfo | undefined = (() => {
        const previouslySelectedRelationshipId =
          retrievePreviouslySelectedRelationshipIdFromStorage();

        if (previouslySelectedRelationshipId) {
          return retrievePreviouslySelectedRelationshipFromStorage(
            previouslySelectedRelationshipId,
            me.relationships
          );
        }

        return undefined;
      })();

      // Selected affiliation
      const selectedAffiliation: AffiliationInfo | undefined = (() => {
        // Get previously selected affiliation id from local storage
        const previouslySelectedAffiliationId =
          retrievePreviouslySelectedAffiliationIdFromStorage();

        // Find previously selected affiliation based on the id stored in the local storage
        if (previouslySelectedAffiliationId) {
          const previouslySelectedAffiliation = retrievePreviouslySelectedAffiliationFromStorage(
            previouslySelectedAffiliationId,
            me.affiliations
          );

          return previouslySelectedAffiliation;
        }

        return undefined;
      })();

      // Session
      const session: Session = createSessionData({me, selectedRelationship, selectedAffiliation});

      if (session.selectedRelationship) {
        updateSelectedRelationshipIdOnStorage(session.selectedRelationship.id);
      } else {
        localStorage.removeItem(LocalStorageKey.SelectedRelationshipId);
      }

      // Log session
      logger.debug('ℹ️ Session restored:', session);

      // Voximplant login
      const callExternalAccountAvailable: boolean = await Voximplant(session);

      // Subscribe to Firebase Cloud Messaging
      // Note: We don't wait for the user's response to continue.
      // Otherwise, the page will load infinitely until the user gives the permissions to receive notifications.
      subscribeFirebasePushNotifications(callExternalAccountAvailable);

      // Update UI
      dispatch({
        type: AuthContextActionType.RESTORE_SESSION,
        firebaseUser,
        session,
      });
    } catch (error) {
      // Something went sideways
      logger.error('🔴 Could not get me (RestorePreviousSession).', error);

      // Cannot continue, logout
      await clearAllUserData();

      // Update UI
      dispatch({
        type: AuthContextActionType.RESTORE_SESSION,
        firebaseUser: undefined,
        session: undefined,
      });
    }
  };

  const hookFirebaseUserAccount = async (firebaseUser: FirebaseUser) => {
    // Rehook backend auth
    await BackendApiManager.instance.hookUser(firebaseUser);

    // Continue process
    restorePreviousSession_withFirebaseUser(firebaseUser);

    // Check for quick access session
    const quickAccessUserData = await QuickAccessManager.instance.getUserData();
    if (quickAccessUserData) {
      await QuickAccessManager.instance.clearUserData();
    }
  };

  // Restore session
  const restorePreviousSession = async () => {
    // If a previous subscription was put in place
    if (onAuthStateChangedSubscription) {
      // Unsubscribe
      onAuthStateChangedSubscription();

      // Null it
      onAuthStateChangedSubscription = null;
    }

    // Subscribe for auth state Change
    onAuthStateChangedSubscription = getAuth().onAuthStateChanged(
      async (firebaseUser: FirebaseUser | null) => {
        // Unsubscribe
        onAuthStateChangedSubscription();

        // Log
        logger.debug('onAuthStateChanged', firebaseUser);

        const quickAccessUserData = await QuickAccessManager.instance.getUserData();

        // Handle user state changes
        if (!firebaseUser && !quickAccessUserData) {
          // No previous session, update UI
          dispatch({
            type: AuthContextActionType.RESTORE_SESSION,
            firebaseUser: undefined,
            session: undefined,
          });
        } else if (firebaseUser) {
          // Hook
          await hookFirebaseUserAccount(firebaseUser);
        } else if (quickAccessUserData) {
          restorePreviousSessionByQuickAccess(quickAccessUserData.userToken, {
            uid: quickAccessUserData.userId!,
            photoURL: null,
            displayName: '',
          } as FirebaseUser);
        }
      }
    );
  };

  // Restore session by Quick Access
  const restorePreviousSessionByQuickAccess = async (
    authToken: string,
    firebaseUser: FirebaseUser
  ) => {
    // reset listeners
    BackendApiManagerEventEmitter.off(BackendApiManagerEvents.QUICK_ACCESS_UNAUTHORIZED, signOut);
    document.removeEventListener('click', resetQuickAccessSessionTimeout);

    // Rehook backend auth
    await BackendApiManager.instance.hookQuickAccess(authToken);

    // Listen for BackendApiManager events
    BackendApiManagerEventEmitter.on(BackendApiManagerEvents.QUICK_ACCESS_UNAUTHORIZED, signOut);

    // Continue process
    restorePreviousSession_withFirebaseUser(firebaseUser);

    document.addEventListener('click', resetQuickAccessSessionTimeout);
    resetQuickAccessSessionTimeout();
  };

  const resetQuickAccessSessionTimeout = () => {
    if (quickAccessSessionTimeout.current) clearTimeout(quickAccessSessionTimeout.current);
    quickAccessSessionTimeout.current = setTimeout(() => signOut(), QuickAccessSessionDuration);
  };

  // Sign in
  const signIn = async (
    credentials: SignInCredentials,
    retryOnWrongPassword: boolean,
    inviteAfterLogin?: InviteIdentification
  ): Promise<SignInError | undefined> => {
    try {
      // Try sign in
      const userCredential: UserCredential = await signInWithEmailAndPassword(
        getAuth(),
        credentials.email,
        credentials.password
      );

      // Rehook backend auth
      await BackendApiManager.instance.hookUser(userCredential.user);

      try {
        // Load session
        const me: Me = await MeRepository.instance.get();

        // Update the user language
        await updateAppLanguage(
          me && me.user && me.user.languageCode ? me.user.languageCode : undefined
        );

        // Session
        const session: Session = createSessionData({me});

        // Log session
        logger.debug('ℹ️ Session signed in:', session);

        // Voximplant login
        const callExternalAccountAvailable: boolean = await Voximplant(session);

        // Subscribe to Firebase Cloud Messaging
        // Note: We don't wait for the user's response to continue.
        // Otherwise, the page will load infinitely until the user gives the permissions to receive notifications.
        subscribeFirebasePushNotifications(callExternalAccountAvailable);

        // If we have an invite to open, set it to open it later
        if (inviteAfterLogin) {
          try {
            officeContext.setInvite(inviteAfterLogin);
          } catch (error) {
            // Log
            logger.debug('🔴 Could not set invite.', error);
          }
        }

        // Login went well
        dispatch({
          type: AuthContextActionType.SIGN_IN,
          firebaseUser: userCredential.user,
          session: session,
        });

        // Done
        return undefined;
      } catch (error) {
        // Something went sideways
        logger.error('🔴 Could not get me (SignIn).', error);

        // Cannot continue, logout
        await clearAllUserData();

        // Update UI
        dispatch({
          type: AuthContextActionType.SIGN_IN,
          firebaseUser: undefined,
          session: undefined,
        });

        // Cannot continue, logout
        return SignInError.FailedToGetMe;
      }
    } catch (error: any) {
      // Error occurred
      logger.debug('🔴 signIn error', error);

      // Pick error
      let signInError: SignInError = SignInError.Unknown;
      switch (error.code) {
        case 'auth/invalid-email':
          // Thrown if the email address is not valid.
          signInError = SignInError.InvalidEmail;
          break;
        case 'auth/user-disabled':
          // Thrown if the user corresponding to the given email has been disabled.
          signInError = SignInError.UserDisabled;
          break;
        case 'auth/user-not-found':
          // Thrown if there is no user corresponding to the given email.
          signInError = SignInError.UserNotFound;
          break;
        case 'auth/wrong-password':
          // Thrown if the password is invalid for the given email, or the account corresponding to the email does not have a password set.
          signInError = SignInError.WrongPassword;
          break;
      }

      // Hey, Hey, Hey! Wait a minute fellow!
      // Let's check one more thing!
      if (retryOnWrongPassword && signInError === SignInError.WrongPassword) {
        try {
          // Ok, think with me on this one:
          // Email exists, bad password?
          // yeah yeah, obvious, one of 2:
          // - User mistaken it's password (forgotten of bad typing)
          // - Someone is trying to access another user account
          // But, what if I told you that it could be a 3rd reason?
          // - Current user is a "legacy" user
          // If this is the case, let's, before return, try to migrate it's old
          // password into Firebase Auth (if old password is right, of course)
          const response = await fetch(
            `${BackendApiManager.instance.getBaseUrl()}/legacy/migrate-account`,
            {
              method: 'POST',
              headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                product: appContext.state.configuration.product,
                email: credentials.email,
                password: credentials.password,
              }),
            }
          );

          // Get Status code
          logger.debug('ℹ️ Login WrongPassword Legacy Migrate StatusCode', response.status);

          // Evaluate
          if (response.status === 200) {
            // Oh yeah, I got it right my friend! Password checks out and it's now updated!
            // Let's try to re-login! It will work for sure this time!
            return signIn(
              {email: credentials.email, password: credentials.password},
              false,
              inviteAfterLogin
            );
          }
          // Well, another thing happened, I failed, but hey! It happens sometimes! :P
          // Let's proceed with the dispatch of the error to UI
        } catch (newError) {
          // Error
          logger.debug('🔴 Login error when calling endpoint /legacy/migrate-account ', newError);
        }
      }

      // Update UI
      dispatch({
        type: AuthContextActionType.SIGN_IN,
        firebaseUser: undefined,
        session: undefined,
      });

      // Return
      return signInError;
    }
  };

  // Sign in (legacy integration)
  const signInLegacyIntegration = async (authToken: string): Promise<SignInError | undefined> => {
    // Rehook Backend Auth
    await BackendApiManager.instance.hookLegacyIntegration(authToken);

    try {
      // Load Session
      const me: Me = await MeRepository.instance.get();

      // Evaluate it
      if (!me || !me.user) {
        // User not found. Could this be a unified account?
        // NOTE: Unified accounts do not work
        return SignInError.UserNotFound;
      }

      // Update the user language
      await updateAppLanguage(
        me && me.user && me.user.languageCode ? me.user.languageCode : undefined
      );

      // Session
      const session: Session = createSessionData({me});

      // Log session
      logger.debug('Sign in session in legacy integration: ', session);

      // If an error did not occurred, login went well, so login and Update UI
      dispatch({
        type: AuthContextActionType.SIGN_IN,
        firebaseUser: {
          name: session.currentUser.name,
          displayName: session.currentUser.name,
          email: session.currentUser.email,
          picture: session.currentUser.picture,
        } as any,
        session: session,
      });

      // Done
      return undefined;
    } catch (error) {
      // Something went sideways
      logger.error('Could not get me (SignInLegacy)', error);

      // Cannot continue, logout
      await clearAllUserData();

      // Error, update UI
      dispatch({
        type: AuthContextActionType.SIGN_IN,
        firebaseUser: undefined,
        session: undefined,
      });

      // Cannot continue, logout
      return SignInError.FailedToGetMe;
    }
  };

  // Sign in (after creating an account)
  const signInAfterCreatingAccount = async (credentials: SignInCredentials) => {
    try {
      // Try sign in
      const userCredential: UserCredential = await signInWithEmailAndPassword(
        getAuth(),
        credentials.email,
        credentials.password
      );

      // Rehook backend auth
      await BackendApiManager.instance.hookUser(userCredential.user);

      try {
        // Load session
        const me: Me = await MeRepository.instance.get();

        // Session
        const session: Session = createSessionData({me});

        // Update the user language
        await updateAppLanguage(
          me && me.user && me.user.languageCode ? me.user.languageCode : undefined
        );

        // Log session
        logger.debug('ℹ️ Session signed in:', session);

        // Voximplant login
        const callExternalAccountAvailable: boolean = await Voximplant(session);

        // Subscribe to Firebase Cloud Messaging
        // Note: We don't wait for the user's response to continue.
        // Otherwise, the page will load infinitely until the user gives the permissions to receive notifications.
        subscribeFirebasePushNotifications(callExternalAccountAvailable);

        // If an error did not occurred, login went well, so login and Update UI
        dispatch({
          type: AuthContextActionType.SIGN_IN,
          firebaseUser: userCredential.user,
          session: session,
        });
      } catch (error) {
        // Something went wrong
        logger.error('🔴 Could not get me (AfterCreatingAccount).', error);

        // Throw error to be caught below
        throw new Error('Could not get me (AfterCreatingAccount).');
      }
    } catch (error: any) {
      // Error occurred
      logger.debug('🔴 signIn error', error);

      // Let's disconnect from everything. We don't know where we left off.
      clearAllUserData();

      // Update UI
      dispatch({
        type: AuthContextActionType.SIGN_IN,
        firebaseUser: undefined,
        session: undefined,
      });

      // Throw the error again to be caught by the screen
      throw new Error(error);
    }
  };

  // Clear all user data
  const clearAllUserData = async () => {
    localStorage.removeItem(LocalStorageKey.SelectedRelationshipId);

    // Close backend
    await BackendApiManager.instance.hookUser(undefined);
    await BackendApiManager.instance.hookQuickAccess(undefined);

    // Sign out from Firebase
    await getAuth()
      .signOut()
      .catch((error: any) => {
        // Log
        logger.debug('🔴 Firebase sign out error in performSignOutAsync: ', error);
      });

    // Reset the app's language to the browser's language
    await updateAppLanguage();

    // Delete API Docs Storage
    localStorage.removeItem(LocalStorageKey.RapiDoc);
  };

  // Sign out in legacy integration
  const signOutLegacyIntegration = async () => {
    localStorage.removeItem(LocalStorageKey.SelectedRelationshipId);

    // Unsubscribe from push notifications
    try {
      await unsubscribeFirebasePushNotifications();
    } catch (error) {
      // Log
      logger.debug(
        '🔴 Could not unsubscribe from push notifications in legacy integration.',
        error
      );
    }

    // Multitasking
    await Promise.all([
      // Close backend
      BackendApiManager.instance.hookUser(undefined),
      BackendApiManager.instance.hookQuickAccess(undefined),
    ]).catch((error) =>
      // Log
      logger.debug('🔴 Could not multitask while signing out.', error)
    );

    // Sign out from Firebase
    await getAuth()
      .signOut()
      .catch((error) =>
        // Log
        logger.debug('🔴 Could not signing out from Firebase.', error)
      );

    // Reset the app's language to the browser's language
    await updateAppLanguage();

    // Update UI
    dispatch({type: AuthContextActionType.SIGN_OUT});
  };

  // Sign out
  const signOut = async () => {
    localStorage.removeItem(LocalStorageKey.SelectedRelationshipId);

    // Remove listeners
    BackendApiManagerEventEmitter.off(BackendApiManagerEvents.QUICK_ACCESS_UNAUTHORIZED, signOut);
    document.removeEventListener('click', resetQuickAccessSessionTimeout);

    try {
      // Unsubscribe from push notifications
      // Note: Always do this first because once we close the backend
      // we won't have a token to delete the push notification subscription.
      // This is best-effort: a push service error must not prevent signing out.
      await unsubscribeFirebasePushNotifications().catch(() => undefined);

      // Multitasking
      await Promise.all([
        // Close backend
        BackendApiManager.instance.hookUser(undefined),
        BackendApiManager.instance.hookQuickAccess(undefined),
      ]).catch((error) =>
        // Log
        logger.debug('🔴 Could not multitask while signing out.', error)
      );

      // Sign out from Voximplant
      await signOutVoximplant();

      // Sign out from Firebase
      await getAuth().signOut();

      // Reset the app's language to the browser's language
      await updateAppLanguage();

      // Delete API Docs Storage
      localStorage.removeItem(LocalStorageKey.RapiDoc);
      localStorage.removeItem(LocalStorageKey.OccurrencesRange);
      localStorage.removeItem(LocalStorageKey.CalendarRange);
      localStorage.removeItem(LocalStorageKey.CalendarDisplay);
      localStorage.removeItem(LocalStorageKey.CareAndActivitiesRange);
      localStorage.removeItem(LocalStorageKey.GeneralChartsRange);
      localStorage.removeItem(LocalStorageKey.EntityGeneralChartsRange);
      localStorage.removeItem(LocalStorageKey.HealthChartsRange);
      await QuickAccessManager.instance.clearUserData();

      // Update UI
      dispatch({type: AuthContextActionType.SIGN_OUT});
    } catch (error: any) {
      // Log
      logger.debug('🔴 Could not sign out.', error);

      // Throw error
      throw new Error(error);
    }
  };

  // Update data session
  const refreshSessionData = async (
    selectedRelationshipId?: string,
    selectedAffiliationId?: string
  ) => {
    try {
      // Get Session
      const me: Me = await MeRepository.instance.get();

      // Selected relationship
      const previouslySelectedRelationshipId =
        selectedRelationshipId ?? retrievePreviouslySelectedRelationshipIdFromStorage();
      let selectedRelationship: RelationshipInfo | undefined = undefined;
      if (previouslySelectedRelationshipId) {
        selectedRelationship = retrievePreviouslySelectedRelationshipFromStorage(
          previouslySelectedRelationshipId,
          me.relationships
        );
      } else if (me.relationships && me.relationships.length > 0) {
        selectedRelationship = me.relationships[0];
      }

      // Selected affiliation
      let selectedAffiliation: AffiliationInfo | undefined = undefined;
      if (selectedAffiliationId) {
        if (me.affiliations && me.affiliations.length > 0) {
          selectedAffiliation = me.affiliations.find(
            (obj) => obj.id.toString() === selectedAffiliationId.toString()
          );
        }
      } else {
        if (me.affiliations && me.affiliations.length > 0) {
          selectedAffiliation = me.affiliations[0];
        }
      }

      // Session
      const session: Session = createSessionData({me, selectedRelationship, selectedAffiliation});

      if (session.selectedRelationship) {
        updateSelectedRelationshipIdOnStorage(session.selectedRelationship.id);
      } else {
        localStorage.removeItem(LocalStorageKey.SelectedRelationshipId);
      }

      // Update session
      dispatch({
        type: AuthContextActionType.UPDATE_SESSION,
        session: session,
      });

      // Log session
      logger.debug('🔄 Session updated: ', session);
    } catch (error: any) {
      // Ops... we encountered an error.
      logger.debug('🔴 Error updateMeData ', error);

      // Pass the Error.
      throw new Error(error);
    }
  };

  // Set current selected affiliation id
  const updateSelectedAffiliationIdOnStorage = (affiliationId: string) =>
    localStorage.setItem(LocalStorageKey.SelectedAffiliationId, affiliationId);

  // Get current selected affiliation id
  const retrievePreviouslySelectedAffiliationIdFromStorage = () =>
    localStorage.getItem(LocalStorageKey.SelectedAffiliationId);

  // Get previously selected affiliation
  const retrievePreviouslySelectedAffiliationFromStorage = (
    previouslySelectedAffiliationId: string,
    affiliations: SessionAffiliationInfo[]
  ): SessionAffiliationInfo | undefined => {
    // Find previous from local storage
    if (previouslySelectedAffiliationId && affiliations.length > 0) {
      const previouslySelectedAffiliation = affiliations.find(
        (affiliation) => affiliation.entity.id === previouslySelectedAffiliationId
      );
      return previouslySelectedAffiliation;
    }
    return undefined;
  };

  // Set current selected relationship id
  const updateSelectedRelationshipIdOnStorage = (relationshipId: string) =>
    localStorage.setItem(LocalStorageKey.SelectedRelationshipId, relationshipId);

  // Get current selected relationship id
  const retrievePreviouslySelectedRelationshipIdFromStorage = () =>
    localStorage.getItem(LocalStorageKey.SelectedRelationshipId);

  // Get previously selected relationship
  const retrievePreviouslySelectedRelationshipFromStorage = (
    previouslySelectedRelationshipId: string,
    relationships: RelationshipInfo[]
  ): RelationshipInfo | undefined => {
    if (previouslySelectedRelationshipId && relationships.length > 0) {
      return relationships.find(
        (relationship) => relationship.id === previouslySelectedRelationshipId
      );
    }
    return undefined;
  };

  // Update selected affiliation
  const updateSelectedAffiliation = (affiliation: SessionAffiliationInfo) => {
    if (upToDateSessionRef.current) {
      // Update
      dispatch({
        type: AuthContextActionType.UPDATE_SESSION,
        session: {
          ...upToDateSessionRef.current,
          selectedAffiliation: affiliation,
        } as Session,
      });

      // Update selected affiliation id on storage
      updateSelectedAffiliationIdOnStorage(affiliation.entity.id);

      // Log
      console.debug('🔄 Selected affiliation updated to', affiliation.entity.name);
    } else {
      // Log
      console.debug('🔴 Selected affiliation not updated. The session was empty.');
    }
  };

  // Update selected relationship
  const updateSelectedRelationship = (relationship: RelationshipInfo) => {
    if (upToDateSessionRef.current) {
      // Update
      dispatch({
        type: AuthContextActionType.UPDATE_SESSION,
        session: {
          ...upToDateSessionRef.current,
          selectedRelationship: relationship,
        } as Session,
      });

      // Update selected relationship id on storage
      updateSelectedRelationshipIdOnStorage(relationship.id);

      // Log
      console.debug('🔄 Selected relationship updated to', relationship.withUser.name);
    } else {
      // Log
      console.debug('🔴 Selected relationship not updated. The session was empty.');
    }
  };

  // Update current user in session
  const updateSessionCurrentUser = (user: User) => {
    if (upToDateSessionRef.current) {
      // Update
      dispatch({
        type: AuthContextActionType.UPDATE_SESSION,
        session: {
          ...upToDateSessionRef.current,
          currentUser: user,
        } as Session,
      });

      // Log
      console.debug('🔄 Current user updated in session to', user);
    } else {
      // Log
      console.debug('🔴 Current user not updated. The session was empty.');
    }
  };

  // Update the unread notification count in session
  const updateSessionUnreadNotificationCount = (count: number) => {
    if (upToDateSessionRef.current) {
      // Update
      dispatch({
        type: AuthContextActionType.UPDATE_SESSION,
        session: {
          ...upToDateSessionRef.current,
          unreadNotificationsCount: count,
        } as Session,
      });

      // Log
      console.debug('🔄 Notification count updated in session to', count);
    } else {
      // Log
      console.debug('🔴 Notification count not updated. The session was empty.');
    }
  };

  // Update the push notifications state
  const setPushNotificationsState = (state: PushNotificationsState) => {
    dispatch({
      type: AuthContextActionType.SET_PUSH_NOTIFICATIONS_STATE,
      pushNotificationsState: state,
    });
  };

  /**
   * Notification permissions and subscribe to push notifications
   *
   * Note: For this to work, we also configured a service worker
   * in the public folder named firebase-messaging-sw.js.
   *
   * IMPORTANT: In MacOS, if the notification doesn't go away after
   * a couple of seconds (4/5 seconds normally), check your system preferences.
   * If the notifications are set to "Banners", they will go away automatically.
   * If the notifications are set to "Alert", they will stay on the screen until dismissed.
   * If they don't appear on the screen at all, you may have set the notifications to "None"
   * on your system preferences.
   *
   * https://firebase.google.com/docs/cloud-messaging/js/client
   */
  const subscribeFirebasePushNotifications = async (subscribeToIncomingCalls: boolean) => {
    try {
      // Check if Firebase Cloud Messaging is supported
      // Note: Not supported on Safari at the moment (05/2022)
      // Firebase Cloud Messaging is supported since Safari 16.1 (11/2024)
      const canSubscribe = await isSupported();

      if (canSubscribe) {
        // Get Firebase app
        const app: FirebaseApp = getApp();

        // Initialize Firebase Cloud Messaging
        const messaging: Messaging = getMessaging(app);

        // Load app config
        const appConfig: AppConfig = require('../../config.json');

        // Ask for notifications permissions and get the registration token
        // Initially this makes a network call, once retrieved
        // subsequent calls to getToken will return from cache.
        // https://firebase.google.com/docs/reference/js/messaging_.gettokenoptions.md#gettokenoptions_interface
        await getToken(messaging, {
          vapidKey: appConfig.firebase.vapidKey,
        })
          .then(async (currentToken) => {
            if (currentToken) {
              // Log
              logger.debug('🟢 FCM token available and permissions given.');

              // Subscribe to foreground push notifications
              onMessage(messaging, (payload) => {
                // Log
                logger.debug('onMessage', payload);

                // Show notification with the Notification API
                if (payload.data) {
                  // Check if it's an incoming call
                  if (payload.data.voximplant) {
                    // Log
                    voxiLogger.debug(
                      'Foreground Voximplant push received. Queueing push handling through the Voximplant recovery flow.'
                    );

                    queueForegroundVoximplantPush(payload.data);

                    // Stop execution here
                    return;
                  }

                  clearPendingIncomingVoximplantCallPlaceholderFromMissedCall(
                    payload.data,
                    'foreground missed-call push received while Voximplant incoming call recovery popup is visible'
                  );

                  eventEmitter.emit(PUSH_NOTIFICATION_RECEIVED, payload.data);

                  // Check if service worker is registered
                  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
                    navigator.serviceWorker.ready.then((registration) => {
                      logger.debug('⏯️ Service worker is available and ready.');
                      registration.showNotification(payload.data?._notificationTitle || '', {
                        body: payload.data?._notificationBody,
                        icon: payload.data?._notificationImageUrl,
                        badge: payload.data?._notificationImageUrl,
                        data: payload.data,
                        vibrate: [300, 100, 400],
                        tag: payload.messageId,
                      } as NotificationOptions);
                    });
                  } else {
                    logger.debug('⏹️ Service worker is not available or not ready.');
                  }

                  // Update the notification count if possible
                  const newCount: number | undefined =
                    payload.data?._notificationsCount &&
                    parseInt(payload.data._notificationsCount, 10)
                      ? parseInt(payload.data._notificationsCount, 10)
                      : undefined;
                  if (newCount) {
                    // Update the unread notifications count
                    updateSessionUnreadNotificationCount(newCount);
                  }
                } else {
                  // Log
                  logger.debug(
                    '⏹️ The notification received onMessage was not shown because the data is empty.'
                  );
                }
              });

              // Send Tokens
              await Promise.all([
                // Send token to backend
                registerNotificationSubscriptionOnBackend(currentToken),

                // Send token to Voximplant
                subscribeToIncomingCalls
                  ? registerNotificationSubscriptionOnVoximplant(currentToken)
                  : Promise.resolve(),
              ]);

              // Set push notifications state
              setPushNotificationsState(PushNotificationsState.Subscribed);

              // Stop execution here
              return;
            } else {
              // Log
              logger.debug('🔴 No FCM token available.');

              // Set push notifications state
              setPushNotificationsState(PushNotificationsState.UnsubscribedError);

              // Stop execution here
              return;
            }
          })
          .catch((error: any) => {
            if (
              Notification.permission !== 'granted' &&
              error.code === 'messaging/permission-blocked'
            ) {
              // Log
              logger.debug(
                '🔴 Could not subscribe to push notifications because the permissions were denied.'
              );

              // Set push notifications state
              setPushNotificationsState(PushNotificationsState.UnsubscribedPermissionsNotGranted);

              // Stop execution here
              return;
            } else {
              // Log
              logger.debug('🔴 Could not retrieve FCM token.', error);

              // Resolve
              setPushNotificationsState(PushNotificationsState.UnsubscribedError);

              // Stop execution here
              return;
            }
          });
      } else {
        // Log
        logger.debug("🔴 Could not subscribe to push notifications because it's not supported.");

        // Set push notifications state
        setPushNotificationsState(PushNotificationsState.UnsubscribedNotSupported);

        // Stop execution here
        return;
      }
    } catch (error) {
      // Log
      logger.debug('🔴 Could not subscribe to push notifications.', error);

      // Set push notifications state
      setPushNotificationsState(PushNotificationsState.UnsubscribedError);

      // Stop execution here
      return;
    }
  };

  /**
   * Unsubscribe from push notifications
   */
  const unsubscribeFirebasePushNotifications = async () => {
    if (
      state.pushNotificationsState === PushNotificationsState.Subscribed ||
      state.pushNotificationsState === PushNotificationsState.Unknown ||
      state.pushNotificationsState === PushNotificationsState.UnsubscribedError
    ) {
      try {
        // Get Firebase app
        const app: FirebaseApp = getApp();

        // Initialize Firebase Cloud Messaging
        const messaging: Messaging = getMessaging(app);

        // Load app config
        const appConfig: AppConfig = require('../../config.json');

        // Get current token
        const currentToken: string = await getToken(messaging, {
          vapidKey: appConfig.firebase.vapidKey,
        });

        // Remove token
        await deleteToken(messaging);

        // Get device code
        const deviceCode: string = getDeviceCode();

        // Delete token from ...
        await Promise.all([
          // ... Backend
          NotificationsRepository.instance.deleteSubscription(deviceCode),

          // ... Voximplant
          unregisterNotificationSubscriptionOnVoximplant(currentToken),
        ]);

        // Remove device id from local storage
        localStorage.removeItem(LocalStorageKey.FcmDeviceId);

        // Reset state
        setPushNotificationsState(PushNotificationsState.Unknown);

        // Log
        logger.debug(`ℹ️ Unsubscribed from push notifications for device code ${deviceCode}.`, {
          pushNotificationsState: state.pushNotificationsState,
        });
      } catch (error: any) {
        // Log
        logger.debug('🔴 Could not unsubscribe from push notifications.', {
          error,
          pushNotificationsState: state.pushNotificationsState,
        });

        // Pass error
        throw new Error(error);
      }
    } else {
      // Log
      logger.debug(
        `ℹ️ No need to unsubscribe since push notifications because state is set to ${state.pushNotificationsState}.`
      );
    }
  };

  const registerNotificationSubscriptionOnBackend = async (token: string) => {
    try {
      // Get device code
      const deviceCode: string = getDeviceCode();

      // Language to use
      let backendLanguage: BackendLanguageTag;
      switch (i18n.language) {
        case AppLanguageTag.enUS:
          backendLanguage = BackendLanguageTag.enUS;

          // Log
          logger.debug(`Notification subscription language set to ${backendLanguage}.`);
          break;
        case AppLanguageTag.esES:
          backendLanguage = BackendLanguageTag.esES;

          // Log
          logger.debug(`Notification subscription language set to ${backendLanguage}.`);
          break;
        case AppLanguageTag.glES:
          backendLanguage = BackendLanguageTag.glES;

          // Log
          logger.debug(`Notification subscription language set to ${backendLanguage}.`);
          break;
        case AppLanguageTag.ptPT:
          backendLanguage = BackendLanguageTag.ptPT;

          // Log
          logger.debug(`Notification subscription language set to ${backendLanguage}.`);
          break;
        case AppLanguageTag.ptBR:
          backendLanguage = BackendLanguageTag.ptBR;

          // Log
          logger.debug(`Notification subscription language set to ${backendLanguage}.`);
          break;
        default:
          backendLanguage = BackendLanguageTag.ptPT;

          // Log
          logger.debug(
            `Notification subscription language set to default ${backendLanguage} because no compatible language was found.`
          );
          break;
      }

      // Make request
      await NotificationsRepository.instance.updateSubscription(deviceCode, {
        token,
        applicationType: 'caregiver',
        applicationName: 'caregiver-web',
        applicationLanguageCode: backendLanguage,
      } as PutNotificationsSubscription);

      // Log
      logger.debug(`🟢 FCM token registered for device code ${deviceCode}.`);
    } catch (error) {
      // Log
      logger.debug('🔴 Could not register for push notifications on backend.', error);
    }
  };

  const getDeviceCode = (): string => {
    // Load app config
    const appConfig: AppConfig = require('../../config.json');

    // Get device OS system
    // https://www.geeksforgeeks.org/detect-the-operating-system-of-user-using-javascript/
    let deviceOS: OperativeSystemName = OperativeSystemName.Other;
    if (navigator.userAgent.toLocaleLowerCase().indexOf('win') !== -1) {
      deviceOS = OperativeSystemName.Windows;
    } else if (navigator.userAgent.toLocaleLowerCase().indexOf('mac') !== -1) {
      deviceOS = OperativeSystemName.MacOS;
    } else if (navigator.userAgent.toLocaleLowerCase().indexOf('linux') !== -1) {
      deviceOS = OperativeSystemName.Linux;
    } else if (navigator.userAgent.toLocaleLowerCase().indexOf('android') !== -1) {
      deviceOS = OperativeSystemName.Android;
    } else if (navigator.userAgent.toLocaleLowerCase().indexOf('like mac') !== -1) {
      deviceOS = OperativeSystemName.iOS;
    }

    // Get device unique ID
    let deviceId: string | null = localStorage.getItem(LocalStorageKey.FcmDeviceId);
    if (!deviceId) {
      // Generate one
      deviceId = uuidv4().split('-').join('');

      // Save it in local storage for later use
      localStorage.setItem(LocalStorageKey.FcmDeviceId, deviceId);
    }

    // Return
    return `${appConfig.product}-${deviceOS}-${deviceId}`;
  };

  const removeRelationship = (relationshipId: string, expectedUserId?: string) => {
    if (
      upToDateSessionRef.current &&
      (!expectedUserId || upToDateSessionRef.current.id === expectedUserId)
    ) {
      // Processed relationships
      const processedRelationships = [...upToDateSessionRef.current.relationships].filter(
        (obj) => obj.id !== relationshipId
      );

      // Selected relationship
      let selectedRelationship = upToDateSessionRef.current.selectedRelationship;
      if (selectedRelationship?.id === relationshipId) {
        // Change the selected relationship
        selectedRelationship =
          processedRelationships.length > 0 ? processedRelationships[0] : undefined;
        if (selectedRelationship) {
          updateSelectedRelationshipIdOnStorage(selectedRelationship.id);
        } else {
          localStorage.removeItem(LocalStorageKey.SelectedRelationshipId);
        }
      }

      // Update
      dispatch({
        type: AuthContextActionType.UPDATE_SESSION,
        session: {
          ...upToDateSessionRef.current,
          relationships: processedRelationships,
          selectedRelationship,
        } as Session,
      });

      // Log
      console.debug('🔄 Relationship removed from session.', {
        relationshipId,
      });
    } else {
      // Log
      console.debug('🔴 Relationship not removed from session. The session was empty.');
    }
  };

  const removeAffiliation = (entityId: string) => {
    if (upToDateSessionRef.current) {
      // Processed affiliations
      const processedAffiliations = [...upToDateSessionRef.current.affiliations].filter(
        (obj) => obj.entity.id !== entityId
      );

      // Selected affiliation
      let selectedAffiliation = upToDateSessionRef.current.selectedAffiliation;
      if (selectedAffiliation?.entity.id === entityId) {
        // Change the selected affiliation
        selectedAffiliation =
          processedAffiliations.length > 0 ? processedAffiliations[0] : undefined;
      }

      // Update
      dispatch({
        type: AuthContextActionType.UPDATE_SESSION,
        session: {
          ...upToDateSessionRef.current,
          affiliations: processedAffiliations,
          selectedAffiliation,
        } as Session,
      });

      // Log
      console.debug('🔄 Affiliation removed from session.', {
        entityId,
      });
    } else {
      // Log
      console.debug('🔴 Affiliation not removed from session. The session was empty.');
    }
  };

  const hasReadPermission = useCallback(
    (rolePermissionType: RolePermissionType): boolean => {
      return (
        state.session?.selectedAffiliation?.rolesPermissionsOverviewKV?.[rolePermissionType]
          ?.canRead ?? false
      );
    },
    [state.session?.selectedAffiliation]
  );

  const hasWritePermission = useCallback(
    (rolePermissionType: RolePermissionType): boolean => {
      return (
        state.session?.selectedAffiliation?.rolesPermissionsOverviewKV?.[rolePermissionType]
          ?.canWrite ?? false
      );
    },
    [state.session?.selectedAffiliation]
  );

  const signInWithMicrosoft = async (inviteAfterLogin?: InviteIdentification) => {
    // Define the provider
    const provider = new OAuthProvider('microsoft.com');
    // const provider = new OAuthProvider('oidc.sioslife-azure-ad');

    // Optional: Specify additional custom OAuth parameters that you want to send with the OAuth request.
    // provider.setCustomParameters({
    //   // Target specific email with login hint.
    //   login_hint: 'andresilva@developmentsioslife.onmicrosoft.com',
    // });

    // Optional: Specify additional OAuth 2.0 scopes beyond basic profile that you want to request from the authentication provider.
    // provider.addScope('mail.read');
    // provider.addScope('calendars.read');
    // provider.addScope('openid');
    // provider.addScope('profile');
    // provider.addScope('email');

    // Force account selection prompt
    provider.setCustomParameters({
      prompt: 'select_account',
    });

    // Sign in with popup
    return signInWithPopup(getAuth(), provider).then(async (result) => {
      // If we have an invite to open, set it to open it later
      if (inviteAfterLogin) {
        try {
          officeContext.setInvite(inviteAfterLogin);
        } catch (error) {
          // Log
          logger.debug('🔴 Could not set invite.', error);
        }
      }

      // Hook
      await hookFirebaseUserAccount(result.user);
    });
  };

  const signInWithGoogle = async (inviteAfterLogin?: InviteIdentification) => {
    // Define the provider
    const provider = new GoogleAuthProvider();

    // Force account selection prompt
    provider.setCustomParameters({
      prompt: 'select_account',
    });

    // Sign in with popup
    return signInWithPopup(getAuth(), provider).then(async (result) => {
      // If we have an invite to open, set it to open it later
      if (inviteAfterLogin) {
        try {
          officeContext.setInvite(inviteAfterLogin);
        } catch (error) {
          // Log
          logger.debug('🔴 Could not set invite.', error);
        }
      }

      // Hook
      await hookFirebaseUserAccount(result.user);
    });
  };

  return (
    <AuthContext.Provider
      value={{
        state,
        // Methods
        restorePreviousSession,
        restorePreviousSessionByQuickAccess,
        signIn,
        signInLegacyIntegration,
        signInAfterCreatingAccount,
        signOut,
        deferVoximplantIncomingPushUntilHealthy,
        recoverVoximplantIncomingPush,
        clearPendingForegroundVoximplantPushRecovery,
        refreshSessionData,
        updateSelectedAffiliation,
        updateSelectedRelationship,
        updateSessionCurrentUser,
        updateSessionUnreadNotificationCount,
        signOutLegacyIntegration,
        setPushNotificationsState,
        subscribeFirebasePushNotifications,
        unsubscribeFirebasePushNotifications,
        removeRelationship,
        removeAffiliation,
        hasReadPermission,
        hasWritePermission,
        signInWithMicrosoft,
        signInWithGoogle,
      }}
    >
      {props.children}
    </AuthContext.Provider>
  );
};
